/**
 * api.ts — base fetch wrapper for React code.
 *
 * Reads the server base URL from ConfigService (via angular-bridge) so there is
 * a single source of truth during the Angular → React migration.
 * Replace with env-var–based config once Angular is fully removed (Phase 6).
 */

import { getConfigService, getTokenService } from './angular-bridge';

// Shape of the server's JSON error body (mirrors the AngularJS $http interceptor's expectations).
export interface ApiErrorBody {
  errors?: Array<{ defaultMessage?: string }>;
  error?: string;
}

// Structured error thrown by every failed request so callers and the global error handler
// can branch on status / read server messages, rather than regex-parsing a string.
// status 0 means the request never reached the server (network failure / CORS / offline).
export class ApiError extends Error {
  constructor(
    readonly method: string,
    readonly path: string,
    readonly status: number,
    readonly body: string,
    readonly data?: ApiErrorBody,
  ) {
    super(`API ${method} ${path} → ${status}: ${body}`);
    this.name = 'ApiError';
  }
}

function buildUrl(path: string): string {
  const [pathname, existingQs] = path.split('?');
  const qs = new URLSearchParams(existingQs ?? '');
  const base = `${getConfigService().getServerBaseUrl()}${pathname}`;
  const qsStr = qs.toString();
  return qsStr ? `${base}?${qsStr}` : base;
}

function authHeaders(): Record<string, string> {
  const token = getTokenService().GetAccessToken?.();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// A 401 with this error means the access token expired and should be refreshed.
// Mirrors the AngularJS interceptor (providers/httpinterceptor.js) check.
const INVALID_TOKEN = 'invalid_token';

// Dedupe concurrent refreshes so a burst of 401s triggers a single /oauth/token call
// (the AngularJS side guards the same way via $rootScope.refreshingToken).
let refreshPromise: Promise<void> | null = null;

function refreshAccessToken(): Promise<void> {
  if (!refreshPromise) {
    const tokenService = getTokenService();
    if (!tokenService?.Exists?.()) return Promise.reject(new Error('No token to refresh'));
    // TokenService.Refresh() POSTs grant_type=refresh_token and stores the new token on success.
    refreshPromise = Promise.resolve(tokenService.Refresh())
      .then(() => undefined)
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

// Refresh window (seconds) — matches TokenService's own proactive-refresh buffer.
const TOKEN_EXPIRY_BUFFER_SEC = 30;

/**
 * Refresh the access token if it is missing, expired, or within the expiry buffer.
 *
 * Call before requests that bypass `request()` (e.g. raw XHR uploads), where a 401-retry
 * would mean re-sending the whole body. No-op when the token is comfortably valid.
 */
export async function ensureFreshToken(): Promise<void> {
  const tokenService = getTokenService();
  if (!tokenService?.Exists?.()) return;
  const expiresAt: number | undefined = tokenService.Get?.()?.expires_at;
  // No expiry info → leave it; a 401 (if any) is still handled reactively elsewhere.
  if (typeof expiresAt !== 'number') return;
  const nowSec = Math.floor(Date.now() / 1000);
  if (expiresAt - nowSec > TOKEN_EXPIRY_BUFFER_SEC) return;
  try {
    await refreshAccessToken();
  } catch {
    // Swallow — the caller proceeds with the current token and any 401 surfaces normally.
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
  retryOn401 = true,
  asText = false,
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(buildUrl(path), {
      method,
      headers: { 'Content-Type': 'application/json', ...authHeaders(), ...extraHeaders },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      // GET responses must never be served from the browser's HTTP cache — a plain refresh
      // (as opposed to a hard/force refresh) would otherwise show stale data.
      cache: method === 'GET' ? 'no-store' : undefined,
    });
  } catch (e) {
    // Request never reached the server (offline, DNS, CORS). Maps to Angular's status-0 branch.
    throw new ApiError(method, path, 0, e instanceof Error ? e.message : String(e));
  }

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    let data: ApiErrorBody | undefined;
    try {
      data = JSON.parse(text) as ApiErrorBody;
    } catch {
      // body was not JSON — leave data undefined, raw text is still on the error
    }

    // Expired token: refresh once, then replay the original request with the new token.
    if (res.status === 401 && retryOn401 && data?.error === INVALID_TOKEN) {
      try {
        await refreshAccessToken();
      } catch {
        throw new ApiError(method, path, res.status, text, data);
      }
      return request<T>(method, path, body, extraHeaders, false, asText);
    }

    throw new ApiError(method, path, res.status, text, data);
  }

  // Plain-text endpoints (e.g. OAuth authorization-url) — return the raw body.
  if (asText) {
    return (await res.text()) as T;
  }

  // 204 No Content or empty body
  const contentType = res.headers.get('content-type') ?? '';
  if (res.status === 204 || !contentType.includes('application/json')) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

// Returns [url, init] for a GET that may be an absolute URL (API or S3 pre-signed).
// - Relative paths always get auth headers via buildUrl.
// - Absolute URLs on the same origin as the API server get auth headers.
// - Absolute URLs on a different origin (e.g. S3 pre-signed) get no auth headers
//   because the credentials are already embedded in the URL's query string.
function resolveUrl(path: string): [string, RequestInit] {
  if (!path.startsWith('http://') && !path.startsWith('https://')) {
    return [buildUrl(path), { headers: authHeaders() }];
  }
  const apiBase = getConfigService().getServerBaseUrl();
  const sameOrigin = path.startsWith(apiBase);
  return [path, { headers: sameOrigin ? authHeaders() : {} }];
}

// Fetch an absolute direct path (e.g. a pre-signed S3 URL) as text, mirroring getArrayBuffer:
// resolveUrl keeps the URL as-is and omits auth for cross-origin, so the API base is not
// prepended. Used for direct HTML/text paths the server returns as absolute S3 URLs.
async function getTextFromUrl(path: string): Promise<string> {
  const res = await fetch(...resolveUrl(path));
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new ApiError('GET', path, res.status, text);
  }
  return res.text();
}

/**
 * Encodes one query-string value the way AngularJS's `$http`/`$resource` did
 * (`encodeUriQuery`): `@ : $ , ;` stay literal and spaces become `+`.
 *
 * The search endpoints are sensitive to this — a query sent as
 * `createdDate%3A+%5B..%5D` instead of `createdDate:+%5B..%5D` does not match the
 * server's field syntax and degrades to an unfiltered search.
 */
export function encodeUriQuery(val: string): string {
  return encodeURIComponent(val)
    .replace(/%40/gi, '@')
    .replace(/%3A/gi, ':')
    .replace(/%24/g, '$')
    .replace(/%2C/gi, ',')
    .replace(/%3B/gi, ';')
    .replace(/%20/g, '+');
}

/** Builds a query string with Angular's encoding; undefined/null values are skipped. */
export function buildQueryString(params: Record<string, string | number | undefined | null>): string {
  return Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${encodeUriQuery(k)}=${encodeUriQuery(String(v))}`)
    .join('&');
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  getText: (path: string): Promise<string> =>
    path.startsWith('http://') || path.startsWith('https://')
      ? getTextFromUrl(path)
      : request<string>('GET', path, undefined, undefined, true, true),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
  getArrayBuffer: async (path: string): Promise<ArrayBuffer> => {
    const res = await fetch(...resolveUrl(path));
    if (!res.ok) throw new Error(`API GET ${path} → ${res.status}`);
    return res.arrayBuffer();
  },
};
